import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../common/Button';
import { useDetectedCity } from '../../hooks/useDetectedCity';
import { cityToSlug } from '../../config/cities';

/**
 * Sticky header with navigation and auth buttons.
 * Collapses to hamburger menu on mobile.
 */
export const Header: React.FC = () => {
  const { user, signOut } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { city } = useDetectedCity();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setIsMenuOpen(false);
  };

  // Close on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  // Close on Escape
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setIsMenuOpen(false);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen, handleKeyDown]);

  const navLinks = user ? (
    <>
      <Link to={`/${cityToSlug(city)}`} onClick={() => setIsMenuOpen(false)}>
        <Button variant="ghost">Tours</Button>
      </Link>
      {user.roles.includes('local_expert') && (
        <Link to="/my-tours" onClick={() => setIsMenuOpen(false)}>
          <Button variant="ghost">My Tours</Button>
        </Link>
      )}
      <Link to="/profile" onClick={() => setIsMenuOpen(false)}>
        <Button variant="ghost">Profile</Button>
      </Link>
      <Button variant="ghost" onClick={handleSignOut}>
        Sign Out
      </Button>
    </>
  ) : (
    <>
      <Link to="/login" onClick={() => setIsMenuOpen(false)}>
        <Button variant="ghost">Login</Button>
      </Link>
      <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
        <Button variant="primary">Sign Up</Button>
      </Link>
    </>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-indigo-velvet-900 bg-indigo-velvet-800" ref={menuRef}>
      {/* Skip link — visually hidden until focused, allows keyboard users to bypass nav */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:rounded focus:bg-indigo-velvet-800 focus:px-4 focus:py-2 focus:text-pale-slate-100 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-golden-hour-400"
      >
        Skip to main content
      </a>
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex flex-shrink-0 items-center">
            <img
              src="/images/waypo-header.png"
              alt="Waypo"
              height={54}
              className="h-[54px]"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-4">
            {navLinks}
          </nav>

          {/* Hamburger button (mobile) */}
          <button
            className="sm:hidden p-[10px] text-pale-slate-300 hover:text-pale-slate-100"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {isMenuOpen && (
        <nav className="sm:hidden border-t border-indigo-velvet-900 bg-indigo-velvet-800 px-4 py-3 flex flex-col gap-1 [&_button]:justify-start">
          {navLinks}
        </nav>
      )}
    </header>
  );
};
